import { lightTheme, type GlobalThemeOverrides, type MessageProviderProps } from 'naive-ui';
import { appendObjectFields } from './functions';
import variables from '../assets/scss/config/variables.module.scss';

type ThemeColors = {
  primaryColor: string;
  primaryColorHover: string;
  secondaryColor: string;
  secondaryColorHover: string;
};

export const messagePlacement: MessageProviderProps['placement'] = 'top-right';

export const combinePropWithTypesAndActions = (
  propName: string,
  propValue: string,
  types: string[],
  actions: string[],
): Record<string, string> => {
  return Object.fromEntries(
    types.flatMap((type) => actions.map((action) => `${propName}${action}${type}`)).map((prop) => [prop, propValue]),
  );
};

const defaultThemeColors: ThemeColors = {
  primaryColor: variables.primaryBlue,
  primaryColorHover: variables.lightBlue2,
  secondaryColor: variables.mediumGreen,
  secondaryColorHover: variables.mediumGreen,
};

const greenThemeColors: ThemeColors = {
  primaryColor: variables.mediumGreen,
  primaryColorHover: variables.mediumGreen,
  secondaryColor: variables.mediumGreen,
  secondaryColorHover: variables.mediumGreen,
};

export const getGlobalThemeOverrides = (themeColors: ThemeColors = defaultThemeColors): GlobalThemeOverrides => {
  return {
    ...getGeneralThemeOverrides(themeColors),
    ...getInputThemeOverrides(themeColors.primaryColor),
    ...getFormThemeOverrides(),
    ...getMenuThemeOverrides(themeColors.secondaryColor),
    ...getMessageThemeOverrides(),
    ...getTableThemeOverrides(),
  };
};

export const getGeneralThemeOverrides = ({
  primaryColorHover,
  primaryColor,
  secondaryColor,
}: ThemeColors): GlobalThemeOverrides => {
  const textColor = variables.white;
  const border = `1px solid ${primaryColorHover}`;
  const actions = ['Hover', 'Pressed', 'Focus'];
  const buttonTypes = ['', 'Primary'];
  // Disabled buttons follow the design system: card surface, disabled gray text, no border. Every
  // type is covered because opacityDisabled removes NaiveUI's fade, and a type left out would keep
  // its enabled colors and look clickable.
  const disabledButtonOverrides = Object.fromEntries(
    ['', 'Primary', 'Error', 'Info', 'Success', 'Warning'].flatMap((type) => [
      [`colorDisabled${type}`, variables.surfaceCard],
      [`textColorDisabled${type}`, variables.textDisabled],
      [`textColorGhostDisabled${type}`, variables.textDisabled],
      [`textColorTextDisabled${type}`, variables.textDisabled],
      [`borderDisabled${type}`, 'none'],
    ]),
  );
  const spinColor = variables.white;
  const dividerColor = variables.darkBlue;
  const switchColor = primaryColorHover;

  return {
    common: {
      primaryColor,
      borderRadius: '5px',
    },
    Divider: {
      color: dividerColor,
    },
    Spin: {
      color: spinColor,
    },
    Switch: {
      railColorActive: switchColor,
    },
    Button: {
      textColor,
      textColorPrimary: textColor,
      ...disabledButtonOverrides,
      opacityDisabled: '1',
      ...combinePropWithTypesAndActions('textColor', textColor, buttonTypes, actions),
      ...combinePropWithTypesAndActions('color', primaryColorHover, buttonTypes, actions),
      border,
      ...combinePropWithTypesAndActions('border', border, buttonTypes, actions),
      colorSecondary: 'transparent',
      colorSecondaryHover: secondaryColor,
      colorQuaternaryHover: 'transparent',
    },
    Tooltip: {
      color: variables.tooltipColor,
    },
    Progress: {
      fillColorSuccess: variables.successColor,
    },
    Layout: {
      headerColor: variables.sidebarBackgroundColor,
      siderColor: variables.sidebarBackgroundColor,
      textColor: variables.labelTextColor,
    },
    Tabs: {
      tabColorSegment: `${primaryColor}FF`,
    },
  };
};

export const getMessageThemeOverrides = (): GlobalThemeOverrides => {
  const maxWidth = '500px';
  const closeIconSize = '20px';
  const lineHeight = '2.5';

  return {
    Message: {
      ...lightTheme.Message,
      colorError: variables.errorColor,
      textColorError: variables.white,
      closeIconColorError: variables.lightskyBlue,
      colorSuccess: variables.successColor,
      textColorSuccess: variables.white,
      closeIconColorSuccess: variables.lightskyBlue,
      colorInfo: variables.lightBlue2,
      textColorInfo: variables.white,
      closeIconColorInfo: variables.lightskyBlue,
      lineHeight: lineHeight,
      maxWidth: maxWidth,
      closeIconSize: closeIconSize,
    },
  };
};

export const getConfirmModalThemeOverrides = (): GlobalThemeOverrides => {
  return {
    Dialog: {
      closeSize: '30px',
      closeIconColor: '#000',
      closeIconColorHover: '#000',
      closeBorderRadius: '.25rem',
      closeMargin: '.5rem .5rem 0 0',
      padding: 0,
      color: variables.grayishBlue,
    },
  };
};

export const getCardThemeOverrides = (): GlobalThemeOverrides => {
  const cardBackgroundColor = variables.grayishBlue;
  const descriptionColor = variables.labelTextColor;
  const textColor = variables.white;
  const fontSize = '14px';
  const titleFontSize = '20px';
  const mediumAndLessPadding = '26px 20px';
  const largeAndMorePadding = '30px 40px';
  const borderRadius = '10px';
  const descriptionMargin = '0 0 10px 0';

  return {
    Card: {
      paddingSmall: mediumAndLessPadding,
      paddingMedium: mediumAndLessPadding,
      paddingLarge: largeAndMorePadding,
      paddingHuge: largeAndMorePadding,
      color: cardBackgroundColor,
      titleFontSizeSmall: titleFontSize,
      titleFontSizeMedium: titleFontSize,
      titleFontSizeLarge: titleFontSize,
      titleFontSizeHuge: titleFontSize,
      fontSizeSmall: fontSize,
      fontSizeMedium: fontSize,
      fontSizeLarge: fontSize,
      fontSizeHuge: fontSize,
      borderColor: cardBackgroundColor,
      borderRadius: borderRadius,
      textColor: textColor,
      titleTextColot: textColor,
    },
    Typography: {
      pTextColor: descriptionColor,
      pMargin: descriptionMargin,
    },
  };
};

export const getFormThemeOverrides = (): GlobalThemeOverrides => {
  return {
    Form: {
      asteriskColor: variables.errorColor,
      feedbackTextColorError: variables.errorColor,
      feedbackFontSizeLarge: '0.875em',
      labelFontSizeTopLarge: '12px',
      labelTextColor: variables.labelTextColor,
    },
  };
};

export const getInputThemeOverrides = (primaryColor: string): GlobalThemeOverrides => {
  const textColor = variables.white;
  const borderType = '1px solid';
  const initBorderColorMap = {
    border: variables.lightBlue,
    borderError: variables.errorColor,
    borderSuccess: variables.successColor,
    borderInfo: variables.lightBlue2,
    borderHover: primaryColor,
    borderDisabled: variables.lightBlue,
    borderFocus: variables.lightBlue,
  };
  const appendedBorderColorMap = appendObjectFields(initBorderColorMap, borderType);

  return {
    Input: {
      textColor,
      color: variables.lightBlue,
      ...appendedBorderColorMap,
      heightLarge: '45px',
      paddingLarge: '0 20px',
      caretColor: textColor,
    },
    InternalSelection: {
      textColor,
      color: variables.lightBlue,
      ...appendedBorderColorMap,
      heightLarge: '45px',
      paddingLarge: '0 20px',
      caretColor: textColor,
    },
    InternalSelectMenu: {
      textColor,
      color: variables.lightBlue,
      ...appendedBorderColorMap,
      heightLarge: '45px',
      paddingLarge: '0 20px',
      caretColor: textColor,
    },
    DatePicker: {
      panelColor: variables.lightBlue,
      itemColorActive: textColor,
      itemTextColor: textColor,
    },
  };
};

export const getDataTableThemeOverrides = (): GlobalThemeOverrides => {
  return {
    DataTable: {
      borderColor: variables.lightGray,
      borderRadius: '0',
      thColor: variables.grayishBlue,
      thColorHover: variables.grayishBlue,
      tdColorHover: variables.grayishBlue,
      tdColor: variables.lightBlue,
      paginationMargin: '12px',
    },
    List: {
      color: 'transparent',
    },
  };
};

export const getTableThemeOverrides = (): GlobalThemeOverrides => {
  return {
    Table: {
      borderColor: variables.lightGray,
      borderRadius: '0',
      thColor: variables.grayishBlue,
      thColorHover: variables.grayishBlue,
      tdColorHover: variables.grayishBlue,
      tdColor: variables.lightBlue,
      paginationMargin: '12px',
    },
    List: {
      color: 'transparent',
    },
  };
};

export const getGreenButtonThemeOverrides = (themeColors: ThemeColors = greenThemeColors): GlobalThemeOverrides => {
  const global = getGlobalThemeOverrides(themeColors);

  return {
    ...global,
    Button: {
      ...global.Button,
      textColor: themeColors.primaryColor,
    },
  };
};

export const getDotsDropdownMenuThemeOverrides = (): GlobalThemeOverrides => {
  return {
    Dropdown: {
      color: variables.darkBlue,
      optionColorHover: variables.darkBlue,
      optionTextColor: variables.white,
      optionTextColorHover: variables.white,
      padding: '14px',
    },
    Icon: {
      color: variables.white,
    },
  };
};

export const getMenuThemeOverrides = (secondaryColor = variables.mediumGreen): GlobalThemeOverrides => {
  return {
    Menu: {
      itemIconColor: variables.menuInactive,
      itemTextColor: variables.menuInactive,
      itemIconColorActive: variables.white,
      itemTextColorActive: variables.white,
      itemTextColorActiveHover: variables.white,
      itemIconColorActiveHover: variables.white,
      itemColorHover: secondaryColor,
      itemColorActive: secondaryColor,
      itemColorActiveHover: secondaryColor,
      itemColorActiveCollapsed: secondaryColor,
    },
  };
};
